import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';

import { Button, ModalV2 as Modal } from '@utilities';
import { updateQuestionTopBottomOptions } from '@api/instant_survey';

import { buildInitialLists, move, reorder } from './utilities/helpers';
import Top2BoxColumn from './components/Top2BoxColumn';
import styles from './_index.module.scss';

const ModalTop2Box = ({ isActive, onClose, question, surveyId }) => {
  const [bottomItems, setBottomItems] = useState([]);
  const [initialBottomIds, setInitialBottomIds] = useState([]);
  const [initialTopIds, setInitialTopIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [poolItems, setPoolItems] = useState([]);
  const [topItems, setTopItems] = useState([]);

  const controllerRef = useRef(null);

  const originalOrderRef = useRef(new Map());

  useEffect(() => {
    if (question?.answers?.length) {
      originalOrderRef.current = new Map(
        question.answers.map((answer, index) => [String(answer.id), index])
      );
    } else {
      originalOrderRef.current = new Map();
    }
  }, [question?.id]);

  const sortByOriginal = useCallback((items) => {
    const originalOrderMap = originalOrderRef.current;

    return items.slice().sort((firstItem, secondItem) => {
      const firstItemIndex = originalOrderMap.get(String(firstItem.id));
      const secondItemIndex = originalOrderMap.get(String(secondItem.id));

      const firstPosition = firstItemIndex ?? Number.POSITIVE_INFINITY;
      const secondPosition = secondItemIndex ?? Number.POSITIVE_INFINITY;

      return firstPosition - secondPosition;
    });
  }, []);

  useEffect(() => {
    if (!isActive || !question) return;
    const { bottom, pool, top } = buildInitialLists(question);
    setTopItems(top);
    setBottomItems(bottom);
    setPoolItems(sortByOriginal(pool));
    setInitialTopIds(top.map((item) => item.id));
    setInitialBottomIds(bottom.map((item) => item.id));
  }, [isActive, question?.id, question?.topNOptions, question?.bottomNOptions, sortByOriginal]);

  const listsById = useMemo(
    () => ({ bottom: bottomItems, pool: poolItems, top: topItems }),
    [bottomItems, poolItems, topItems]
  );

  const setListById = useCallback((listId, nextItems) => {
    if (listId === 'bottom') setBottomItems(nextItems);
    else if (listId === 'pool') setPoolItems(nextItems);
    else setTopItems(nextItems);
  }, []);

  const insertIntoPool = useCallback(
    (items) => {
      if (!items?.length) return;
      setPoolItems((previousPool) => {
        const existingIds = new Set(previousPool.map((item) => item?.id));
        const mergedPool = [...previousPool];
        for (const item of items) {
          if (!existingIds.has(item?.id)) mergedPool.push(item);
        }
        return sortByOriginal(mergedPool);
      });
    },
    [sortByOriginal]
  );

  const removeFromList = useCallback(
    (listId, itemId) => {
      const listItems = listsById[listId];
      const removeIndex = listItems.findIndex((item) => item.id === itemId);
      if (removeIndex === -1) return;

      const nextItems = listItems.slice();
      const [removedItem] = nextItems.splice(removeIndex, 1);

      setListById(listId, nextItems);
      insertIntoPool([removedItem]);
    },
    [listsById, insertIntoPool, setListById]
  );

  const clearList = useCallback(
    (listId) => {
      const listItems = listsById[listId];
      if (!listItems?.length) return;
      setListById(listId, []);
      insertIntoPool(listItems);
    },
    [listsById, insertIntoPool, setListById]
  );

  const onDragEnd = useCallback(
    (result) => {
      const { destination, source } = result;
      if (!destination) return;

      if (source.droppableId === 'pool' && destination.droppableId === 'pool') {
        setPoolItems((prevPool) => sortByOriginal(prevPool));
        return;
      }

      if (source.droppableId === destination.droppableId) {
        const reordered = reorder(listsById[source.droppableId], source.index, destination.index);
        setListById(source.droppableId, reordered);
        return;
      }

      const destinationAppendIndex = listsById[destination.droppableId].length;
      const adjustedDestination = { ...destination, index: destinationAppendIndex };

      const moved = move(
        listsById[source.droppableId],
        listsById[destination.droppableId],
        source,
        adjustedDestination
      );

      if (source.droppableId === 'pool') {
        setPoolItems(sortByOriginal(moved.pool));
      } else {
        setListById(source.droppableId, moved[source.droppableId]);
      }

      if (destination.droppableId === 'pool') {
        setPoolItems(sortByOriginal(moved.pool));
      } else {
        setListById(destination.droppableId, moved[destination.droppableId]);
      }
    },
    [listsById, setListById, sortByOriginal]
  );

  const hasChanges = useMemo(() => {
    const currentTopIds = topItems.map((item) => item.id);
    const currentBottomIds = bottomItems.map((item) => item.id);

    const sameTop = JSON.stringify(currentTopIds) === JSON.stringify(initialTopIds);
    const sameBottom = JSON.stringify(currentBottomIds) === JSON.stringify(initialBottomIds);

    return !(sameTop && sameBottom);
  }, [topItems, bottomItems, initialTopIds, initialBottomIds]);

  const handleApply = useCallback(async () => {
    if (!question?.id || !surveyId) return;

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    const payload = {
      bottomNOptions: bottomItems.map((item) => Number(item.id)),
      questionId: question.id,
      signal,
      surveyId,
      topNOptions: topItems.map((item) => Number(item.id)),
    };

    try {
      setIsSaving(true);
      await updateQuestionTopBottomOptions(payload);
      onClose();
    } catch (error) {
      if (error && error.name === 'CanceledError') return;
      console.error('Failed to update Top 2 Box settings:', error);
    } finally {
      setIsSaving(false);
    }
  }, [bottomItems, onClose, question?.id, surveyId, topItems]);

  return (
    <Modal
      buttons={[
        <Button key="cancel-btn" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button
          disabled={isSaving || !hasChanges}
          key="submit-btn"
          onClick={handleApply}
          text="Apply Changes"
        />,
      ]}
      isActive={isActive}
      isScrollable
      onClose={onClose}
      size="large"
      title="Top 2 Box"
    >
      <div className={styles['modal-top-2-box']}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Top2BoxColumn
            id="top"
            items={topItems}
            onClear={() => clearList('top')}
            onRemoveItem={(item) => removeFromList('top', item.id)}
            placeholder="Drag answer options to combine them for the top box."
            title="Top"
          />

          <Top2BoxColumn
            id="bottom"
            items={bottomItems}
            onClear={() => clearList('bottom')}
            onRemoveItem={(item) => removeFromList('bottom', item.id)}
            placeholder="Drag answer options to combine them for the bottom box."
            title="Bottom"
          />

          <Top2BoxColumn
            id="pool"
            items={poolItems}
            placeholder="All answer options have
been combined."
          />
        </DragDropContext>
      </div>
    </Modal>
  );
};

ModalTop2Box.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  question: PropTypes.shape({
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        text: PropTypes.string,
      })
    ),
    bottomNOptions: PropTypes.arrayOf(PropTypes.number),
    id: PropTypes.number,
    text: PropTypes.string,
    topNOptions: PropTypes.arrayOf(PropTypes.number),
  }),
  surveyId: PropTypes.number,
};

export default ModalTop2Box;
